import { useCallback, useState, type ReactNode } from 'react';

import { InformationCircleIcon } from '@heroicons/react/20/solid';

interface Props {
  label?: string;
  children: ReactNode;
  widthClass?: string;
}

// Lightweight click-to-toggle info popover. A full-screen transparent backdrop
// catches outside clicks to close. Use for short methodology / "how this is
// calculated" explainers next to a chart or metric.
export function InfoPopover({ label = 'How this is calculated', children, widthClass = 'w-80' }: Props) {
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex items-center gap-1 text-[11px] font-medium text-gray-400 hover:text-gray-600"
      >
        <InformationCircleIcon className="h-4 w-4" />
        {label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" aria-hidden onClick={close} />
          <div
            className={`absolute right-0 z-20 mt-1.5 ${widthClass} rounded-lg border border-gray-200 bg-white p-3 text-left text-xs leading-relaxed text-gray-600 shadow-lg`}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
}
